import {getSoundIdByUrl} from "./notificationSounds";

const NOTIFICATION_SETTINGS_KEY = "notification_settings";

export const DEFAULT_SETTINGS = {
  volume: 70,
  tone: "default",
  enabled: true,
};

function sanitizeVolume(volume) {
  const parsed = Number(volume);
  if (!Number.isFinite(parsed)) return DEFAULT_SETTINGS.volume;
  return Math.max(0, Math.min(100, parsed));
}

function sanitizeTone(tone) {
  return typeof tone === "string" && tone.trim()
    ? tone
    : DEFAULT_SETTINGS.tone;
}

function sanitizeEnabled(enabled) {
  return typeof enabled === "boolean" ? enabled : DEFAULT_SETTINGS.enabled;
}

function saveRawSettings(settings) {
  localStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(settings));
}

export function getNotificationPreferences() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_SETTINGS_KEY);

    if (raw === null || raw === undefined) {
      return {...DEFAULT_SETTINGS};
    }

    try {
      const parsed = JSON.parse(raw);

      if (typeof parsed === "object" && parsed !== null) {
        const normalized = {
          volume: sanitizeVolume(parsed.volume),
          tone: sanitizeTone(parsed.tone),
          enabled: sanitizeEnabled(parsed.enabled),
        };

        saveRawSettings(normalized);
        return normalized;
      }
    } catch (e) {
      console.log(
        "No se pudo parsear como objeto, intentando como número directo:",
        e,
      );
    }

    const volume = Number(raw);
    if (!isNaN(volume)) {
      const normalized = {
        ...DEFAULT_SETTINGS,
        volume: sanitizeVolume(volume),
      };
      saveRawSettings(normalized);
      return normalized;
    }

    return {...DEFAULT_SETTINGS};
  } catch (error) {
    console.error("Error al obtener configuración de notificaciones:", error);
    return {...DEFAULT_SETTINGS};
  }
}

export function getNotificationSettings() {
  return getNotificationPreferences().volume;
}

export function saveNotificationSettings(volume) {
  try {
    const current = getNotificationPreferences();
    const next = {
      ...current,
      volume: sanitizeVolume(volume),
    };
    saveRawSettings(next);
  } catch (error) {
    console.error("Error guardando el volumen en localStorage:", error);
    throw error;
  }
}

export function saveNotificationPreferences({volume, tone, enabled} = {}) {
  try {
    const current = getNotificationPreferences();
    const next = {
      volume: sanitizeVolume(volume ?? current.volume),
      tone: sanitizeTone(tone ?? current.tone),
      enabled: sanitizeEnabled(enabled ?? current.enabled),
    };

    saveRawSettings(next);
    return next;
  } catch (error) {
    console.error("Error guardando configuración de notificaciones:", error);
    throw error;
  }
}

/**
 * Decide si debe mostrarse una notificación (sonido, snackbar, desktop) según
 * notification_settings del agente. Un mensaje pertenece al inbox personal si
 * contact.id_agent === agentId; cualquier otro caso (otro agente, bot o id_agent
 * ausente) se trata como notificación de otros inboxes.
 */
export function shouldShowNotification(notificationSettings, {agentId, contactAgentId} = {}) {
  if (!notificationSettings) return true;
  if (notificationSettings.global_mute === true) return false;
  const isPersonal = String(contactAgentId) === String(agentId);
  const inbox = isPersonal
    ? notificationSettings.personal_inbox
    : notificationSettings.other_inboxes;
  return !inbox || inbox.enabled !== false;
}

/**
 * Resuelve el id de tono que debe sonar según el inbox al que pertenece el
 * mensaje. Sin notification_settings (agente legacy) cae al tono local; con
 * settings pero tone null ("sin tono") devuelve null → silencio.
 */
export function resolveInboxTone(notificationSettings, {agentId, contactAgentId, fallbackTone} = {}) {
  if (!notificationSettings) return fallbackTone ?? null;
  const isPersonal = String(contactAgentId) === String(agentId);
  const inbox = isPersonal
    ? notificationSettings.personal_inbox
    : notificationSettings.other_inboxes;
  const toneUrl = inbox?.tone;
  return toneUrl ? getSoundIdByUrl(toneUrl) : null;
}
